import React from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import {ServiceEvent} from "crqlar-react-common/dist/models/agenda/agenda.types";
import AgendaEventDialogPreview from "./AgendaEventDialogPreview";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface Props {
    event?: ServiceEvent;
    open: boolean;
    onClose: () => void;
    defaultLanguageCode: string;
    locale: Record<string, string>;
    agenda?: AgendaV3 | undefined
    refetch: () => void
}

const AgendaEventDialog: React.FC<Props> = ({
                                                event,
                                                open,
                                                onClose,
                                                defaultLanguageCode,
                                                locale,
                                                agenda,
                                                refetch,
                                            }) => {
    const handleClose = () => {
        onClose();
    }

    const [scrolled, setScrolled] = React.useState(false);
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) =>
        setScrolled((e.currentTarget.scrollTop ?? 0) > 0);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    if (!event) return null;

    const {
        service,
        earliestStart,
        latestEnd,
        reservationCount,
        remainingSeats
    } = event.event;

    const title = event.title
    const serviceDescription = service.shortDescriptionLocalized?.[defaultLanguageCode] || service.descriptionLocalized?.[defaultLanguageCode];

    const start = new Date(earliestStart);
    const end = new Date(latestEnd);

    const bookingEngineId = service?.bookingEngineId;
    const isBookingEnabled = !!agenda?.enableBooking && !!bookingEngineId;
    const isFullyBooked = (remainingSeats ?? 0) <= 0;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={fullScreen}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        height: fullScreen ? "100dvh" : "auto",
                        maxHeight: fullScreen ? "100dvh" : "calc(100% - 96px)",
                        display: "flex",
                    },
                }}
            >
                <DialogTitle sx={{
                    pb: 1.5,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: 'background.paper',
                    borderBottom: scrolled ? 1 : 0,
                    borderColor: 'divider',
                }}>
                    <Box>
                        {service.media?.mediaURI && (
                            <Avatar
                                src={service.media?.mediaURI}
                                alt={service.name}
                                sx={{
                                    width: "100%",
                                    height: 148,
                                    bgcolor: "grey.300",
                                    mb: 3,
                                    borderRadius: 2,
                                }}
                            >
                                {title}
                            </Avatar>
                        )}
                        <Typography variant="h4" fontWeight={700}>
                            {title}
                        </Typography>
                        {serviceDescription && (
                            <Typography variant="h6" color="text.secondary">
                                {serviceDescription}
                            </Typography>
                        )}
                    </Box>
                </DialogTitle>

                <DialogContent
                    onScroll={handleScroll}
                    sx={{
                        pt: 5,
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'contain'
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <AccessTimeIcon color="action"/>
                        <Typography variant="body1" fontWeight={500}>
                            {formatDateHM(start)} - {formatDateHM(end)}
                        </Typography>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        my: 2,
                        flexWrap: 'wrap'
                    }}>
                        <Chip
                            label={`${reservationCount} ${locale["Participants"] || "Participants"}`}
                            variant="outlined"
                            color="primary"
                        />
                        <Chip
                            label={`${remainingSeats} ${locale["Seats Remaining"] || "Seats Remaining"}`}
                            variant="outlined"
                            color="primary"
                        />
                    </Box>

                    {isBookingEnabled && (
                        isFullyBooked ? (
                            <Alert severity="info" sx={{my: 3}}>
                                {locale["This event is fully booked"] || "This event is fully booked"}
                            </Alert>
                        ) : (
                            <Box
                                sx={{display: "flex", alignItems: "center", my: 6}}>
                                <AgendaEventDialogPreview
                                    widgetId={bookingEngineId}
                                    agendaId={agenda?.id}
                                    selectedServiceId={service.id}
                                    initialStartTime={earliestStart}
                                    onCompletedCallback={() => {
                                        refetch();
                                        handleClose();
                                    }}
                                    selectedSectorId={service.sectorIds?.at(0)}
                                />
                            </Box>
                        )
                    )}

                </DialogContent>

                <DialogActions sx={{
                    p: 2,
                    borderTop: "1px solid rgb(220, 220, 220)"
                }}>
                    {!agenda?.enableBooking ? (
                        <Button onClick={onClose}>
                            {locale["Close"] || "Close"}
                        </Button>
                    ) : (
                        <Button variant="outlined" onClick={onClose}>
                            {locale["Cancel"] || "Cancel"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AgendaEventDialog;
